import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { NGXLogger as LoggerService } from 'ngx-logger';
import { paths } from 'functions-lib';
import { SamplePollLanguage } from 'whocan-lib';

/**
 * Index-Node `samplePollSlugs/{slug}/{lang}` -> `{ ownerId, pollId }`.
 *
 * Wird vom Admin gepflegt wenn ein Poll als Sample-Poll in einer bestimmten
 * Sprache publiziert wird. Erlaubt direktes Lookup einer kurzen, SEO-tauglichen
 * URL (`/{lang}/poll-templates/{slug}`) auf das eigentliche Poll-Tupel — und
 * dank Multi-Lang-Schema kann derselbe Slug parallel pro Sprache eine eigene
 * Poll-Variante haben.
 *
 * Resolver-Strategie (siehe sample-poll.resolver.ts): zuerst die User-Sprache
 * versuchen, dann EN-Fallback, dann 404.
 */
export interface SamplePollSlugEntry {
    ownerId: string;
    pollId: string;
}

export interface SamplePollSlugListEntry extends SamplePollSlugEntry {
    slug: string;
    lang: SamplePollLanguage;
}

export interface SamplePollLanguageVariant {
    lang: SamplePollLanguage;
    ownerId: string;
    pollId: string;
}

@Injectable({
    providedIn: 'root'
})
export class SamplePollSlugService {

    constructor(
        private logger: LoggerService,
        private afd: AngularFireDatabase
    ) { }

    private slugPath(slug: string): string {
        return paths.samplePollSlugs + '/' + slug;
    }

    private slugLangPath(slug: string, lang: SamplePollLanguage): string {
        return paths.samplePollSlugs + '/' + slug + '/' + lang;
    }

    /**
     * Liefert `{ownerId, pollId}` fuer einen Sample-Poll-Slug in einer
     * bestimmten Sprache. Gibt `null` zurueck wenn der `{slug, lang}`-Pfad
     * leer ist. KEIN automatischer Fallback auf EN — Aufgabe des Resolvers.
     */
    public async getPollKeysBySlug(slug: string, lang: SamplePollLanguage): Promise<SamplePollSlugEntry | null> {
        if (!slug || !lang) return null;
        const snapshot = await this.afd.database.ref(this.slugLangPath(slug, lang)).once('value');
        if (!snapshot.exists()) {
            this.logger.log('SamplePollSlugService.getPollKeysBySlug: not found', slug, lang);
            return null;
        }
        const value = snapshot.val();
        if (!value?.ownerId || !value?.pollId) {
            this.logger.warn('SamplePollSlugService.getPollKeysBySlug: invalid entry', slug, lang, value);
            return null;
        }
        return { ownerId: value.ownerId, pollId: value.pollId };
    }

    /**
     * Setzt oder ueberschreibt den Slug-Index-Eintrag fuer eine bestimmte
     * Sprache. Andere Sprach-Varianten desselben Slugs bleiben unberuehrt.
     */
    public async setSlug(slug: string, lang: SamplePollLanguage, ownerId: string, pollId: string): Promise<void> {
        this.logger.log('SamplePollSlugService.setSlug', slug, lang, ownerId, pollId);
        await this.afd.database.ref(this.slugLangPath(slug, lang)).set({ ownerId, pollId });
    }

    /**
     * Entfernt einen Slug-Eintrag fuer eine bestimmte Sprache. Andere
     * Sprach-Varianten desselben Slugs bleiben unberuehrt.
     */
    public async removeSlug(slug: string, lang: SamplePollLanguage): Promise<void> {
        if (!slug || !lang) return;
        this.logger.log('SamplePollSlugService.removeSlug', slug, lang);
        await this.afd.database.ref(this.slugLangPath(slug, lang)).remove();
    }

    /**
     * Liefert alle existierenden Sprach-Varianten fuer einen Slug.
     * Wird fuer hreflang-Tag-Generierung verwendet (siehe RouteContextService).
     */
    public async getAllLanguageVariants(slug: string): Promise<SamplePollLanguageVariant[]> {
        if (!slug) return [];
        const snapshot = await this.afd.database.ref(this.slugPath(slug)).once('value');
        if (!snapshot.exists()) return [];
        const result: SamplePollLanguageVariant[] = [];
        snapshot.forEach(child => {
            const value = child.val();
            if (value?.ownerId && value?.pollId) {
                result.push({ lang: child.key as SamplePollLanguage, ownerId: value.ownerId, pollId: value.pollId });
            }
            return false;
        });
        return result;
    }

    /**
     * Liefert alle aktuell publizierten `{slug, lang}`-Kombinationen.
     * Wird vom Admin-UI und vom Listing genutzt.
     */
    public async listAllSlugs(): Promise<SamplePollSlugListEntry[]> {
        const snapshot = await this.afd.database.ref(paths.samplePollSlugs).once('value');
        if (!snapshot.exists()) return [];
        const result: SamplePollSlugListEntry[] = [];
        snapshot.forEach(slugChild => {
            const slug = slugChild.key;
            slugChild.forEach(langChild => {
                const value = langChild.val();
                if (value?.ownerId && value?.pollId) {
                    result.push({
                        slug,
                        lang: langChild.key as SamplePollLanguage,
                        ownerId: value.ownerId,
                        pollId: value.pollId,
                    });
                }
                return false;
            });
            return false;
        });
        return result;
    }
}
